import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getMarketplaceLedger } from "../../api/myboothshareBilling";
import RentalsShell from "../manager/rentals/RentalsShell";
import { useAuth } from "../../context/AuthContext";
import {
  formatMarketplaceDateTime,
  formatMarketplaceMoneyFromCents,
  getMarketplaceBookingLabel,
  getMarketplaceReleaseLabel,
} from "../../utils/myboothshareBillingPresentation";

function LedgerBadge({ children, tone = "slate" }) {
  const className =
    tone === "emerald"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : tone === "amber"
      ? "border-amber-200 bg-amber-50 text-amber-700"
      : tone === "rose"
      ? "border-rose-200 bg-rose-50 text-rose-700"
      : "border-slate-200 bg-slate-100 text-slate-700";
  return (
    <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${className}`}>
      {children}
    </span>
  );
}

export default function OperatorLedgerPage() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getMarketplaceLedger(token)
      .then((response) => {
        if (active) {
          setItems(Array.isArray(response?.items) ? response.items : []);
        }
      })
      .catch((err) => {
        if (active) {
          setError(
            err?.response?.data?.detail || err.message || "Unable to load ledger."
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  const metrics = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        acc.totalGross += Number(item.amount_cents || 0);
        acc.totalNet += Number(item.net_payout_cents || 0);
        acc.totalFees += Number(item.platform_fee_cents || 0);
        acc.totalRefunds += Number(item.refund_total_cents || 0);
        return acc;
      },
      {
        totalGross: 0,
        totalNet: 0,
        totalFees: 0,
        totalRefunds: 0,
      }
    );
  }, [items]);

  return (
    <RentalsShell
      eyebrow="Ellyanna Boothshare Billing"
      title="Transaction ledger"
      description="Review renter payments, pending releases, released transfers, refunds, disputes, and estimated release dates in one finance-facing ledger."
      actions={
        <Link
          to="/account/operator/finance"
          className="rounded-full bg-[#2f3566] px-4 py-2 text-sm font-semibold text-white hover:bg-[#252a53]"
        >
          Open admin controls
        </Link>
      }
    >
      <section className="space-y-6">
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <article className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Paid by renter
            </div>
            <div className="mt-3 text-3xl font-semibold text-slate-950">
              {formatMarketplaceMoneyFromCents(metrics.totalGross, "cad")}
            </div>
          </article>
          <article className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Net payout
            </div>
            <div className="mt-3 text-3xl font-semibold text-slate-950">
              {formatMarketplaceMoneyFromCents(metrics.totalNet, "cad")}
            </div>
          </article>
          <article className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Platform fees
            </div>
            <div className="mt-3 text-3xl font-semibold text-slate-950">
              {formatMarketplaceMoneyFromCents(metrics.totalFees, "cad")}
            </div>
          </article>
          <article className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Refunds
            </div>
            <div className="mt-3 text-3xl font-semibold text-slate-950">
              {formatMarketplaceMoneyFromCents(metrics.totalRefunds, "cad")}
            </div>
          </article>
        </section>
        {loading ? <p className="text-sm text-slate-600">Loading ledger...</p> : null}
        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}
        {!loading && !error && !items.length ? (
          <div className="rounded-[1.75rem] border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-slate-600">
            No marketplace billing activity has been recorded for this salon yet.
          </div>
        ) : null}
        <section className="grid gap-4">
          {items.map((item) => {
            const tone = item.dispute_open
              ? "amber"
              : String(item.transfer_status || "").toLowerCase() === "released"
              ? "emerald"
              : item.refund_total_cents
              ? "rose"
              : "slate";
            return (
              <article
                key={item.booking_id}
                className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-500">
                      Booking #{item.booking_id}
                    </p>
                    <h2 className="mt-2 text-2xl font-semibold text-slate-950">
                      Workspace #{item.workspace_id || "n/a"}
                    </h2>
                    <p className="mt-2 text-sm text-slate-600">
                      {formatMarketplaceDateTime(item.start_at_utc)} to{" "}
                      {formatMarketplaceDateTime(item.end_at_utc)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <LedgerBadge tone={tone}>{getMarketplaceReleaseLabel(item)}</LedgerBadge>
                    <LedgerBadge>{getMarketplaceBookingLabel(item.booking_status)}</LedgerBadge>
                  </div>
                </div>

                <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Paid by renter
                    </div>
                    <div className="mt-2 text-base font-semibold text-slate-950">
                      {formatMarketplaceMoneyFromCents(item.amount_cents, item.currency)}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Pending release
                    </div>
                    <div className="mt-2 text-base font-semibold text-slate-950">
                      {formatMarketplaceMoneyFromCents(item.net_payout_cents, item.currency)}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Platform fee
                    </div>
                    <div className="mt-2 text-base font-semibold text-slate-950">
                      {formatMarketplaceMoneyFromCents(item.platform_fee_cents, item.currency)}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Refunded
                    </div>
                    <div className="mt-2 text-base font-semibold text-slate-950">
                      {formatMarketplaceMoneyFromCents(item.refund_total_cents, item.currency)}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Estimated release
                    </div>
                    <div className="mt-2 text-base font-semibold text-slate-950">
                      {item.released_at_utc
                        ? formatMarketplaceDateTime(item.released_at_utc)
                        : item.scheduled_release_at_utc
                        ? formatMarketplaceDateTime(item.scheduled_release_at_utc)
                        : "Pending"}
                    </div>
                  </div>
                </div>
                {item.renter_email ? (
                  <div className="mt-4 text-sm text-slate-600">
                    Renter: <span className="font-semibold text-slate-900">{item.renter_email}</span>
                  </div>
                ) : null}
              </article>
            );
          })}
        </section>
      </section>
    </RentalsShell>
  );
}
